import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { dateDiff } from "~/helpers/date";
import { WorkflowListSteps } from "~/molecules/WorkflowListSteps";
import { Link } from "react-router";
import { type DocumentVersionSummary } from "~/api/types";
import SimpleDataTable, { type TableColumn } from "~/molecules/SimpleDataTable";
import { DueDateCell } from "~/molecules/DueDateCell";
import { useDocumentVersions, QueryParams } from "~/api/v2/useDocumentVersions";
import { DocumentVersionStatus } from "~/config/types";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { FilterPopover } from "~/molecules/FilterPopover";
import SwitchWithLabel from "~/molecules/SwitchWithLabel";
import SearchBar from "~/molecules/SearchBar";
import { useFilter, type Filter } from "~/hooks/useFilter";
import { useEnhancedFilters } from "~/helpers/legalEntities";
import { useTags } from "~/api/swr/useTags";
import { useTiers } from "~/api/swr/useTiers";
import { useCategories } from "~/api/swr/useCategories";
import { extractOwners } from "~/helpers/workflow";
import DocumentIcon from "~/molecules/DocumentIcon";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { useUserContext } from "~/hooks/useUserContext";
import { tabs } from "~/pages/WorkflowsPage";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";
import { MyWorkflowStepsCell } from "~/molecules/MyWorkflowStepsCell";
import {
  getNoTierLabel,
  getTierTitle,
  mapTierOptions,
  tierIdToFormValue,
} from "~/helpers/tier";
import { Toolbar } from "~/molecules/Toolbar";

export const WorkflowList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allDocumentVersions, isLoading } = useDocumentVersions({
    statuses: [DocumentVersionStatus.draft],
    includeWorkflowData: true,
    titleSearch: searchParams.get(QueryParams.Title),
  });
  const activeTab = searchParams.get("tab") ?? tabs.ongoing;

  const documentVersions = allDocumentVersions?.filter((dv) =>
    activeTab === tabs.ongoing ? dv.isOngoing : !dv.isOngoing,
  );

  const { data: tags } = useTags();
  const { data: tiers } = useTiers();
  const { data: categories } = useCategories();
  const { t } = useTranslation();
  const { user } = useUserContext();
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);

  const [expandAll, setExpandAll] = useState(false);

  const baseFilterInput: Filter<DocumentVersionSummary>[] =
    categories && tiers && documentVersions && tags
      ? [
          {
            id: "category",
            label: t(($) => $.commons.document.category),
            options: categories.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (dv) => `${dv.document.category.id}`,
          },
          {
            id: "owner",
            label: t(($) => $.commons.document.owner),
            options: extractOwners(
              documentVersions.map((dv) => dv.document.owner),
              user.id,
            ),
            getValue: (dv) => `${dv.document.owner.userId}`,
          },
          {
            id: "tag",
            label: t(($) => $.commons.document.tags),
            options: tags.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (dv) => dv.document.tagIds.map((id) => `${id}`),
          },
          {
            id: "tier",
            label: t(($) => $.commons.document.tiers),
            options: mapTierOptions(tiers, t) ?? [],
            getValue: (dv) => tierIdToFormValue(dv.document.tier?.id),
          },
        ]
      : [];

  const filterInput = useEnhancedFilters(
    baseFilterInput,
    (dv) => dv.document.id,
  );

  const {
    filters,
    activeFilters,
    handleClearFilters,
    handleSelectFilter,
    filterItems,
  } = useFilter(filterInput);

  const filteredDocumentVersions = useMemo(
    () => filterItems(documentVersions ?? []),
    [documentVersions, filterItems],
  );

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!allDocumentVersions?.length && !searchParams.get(QueryParams.Title)) {
    return <NoResult />;
  }

  const allColumns: TableColumn<DocumentVersionSummary>[] = [
    ...(isRefNumEnabled
      ? [
          {
            columnKey: "commons.document.referenceNumber",
            header: t(($) => $.commons.document.referenceNumber),
            compareFn: (a: DocumentVersionSummary, b: DocumentVersionSummary) =>
              (a.document.referenceNumber ?? "").localeCompare(
                b.document.referenceNumber ?? "",
              ),
            cell: (i: DocumentVersionSummary) => (
              <span>{i.document.referenceNumber ?? "-"}</span>
            ),
          },
        ]
      : []),
    {
      columnKey: "commons.title",
      header: t(($) => $.commons.title),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (i) => (
        <Link
          className="flex items-center gap-1 hover:underline"
          to={`/documents/${i.document.id}?versionId=${i.id}&tab=workflow`}
        >
          <DocumentIcon type={i.fileType} className="size-5 shrink-0" />
          {i.title}
        </Link>
      ),
    },
    {
      columnKey: "commons.workflow.mySteps",
      header: t(($) => $.commons.workflow.mySteps),
      compareFn: (a, b) => {
        const aHasParticipant = (a.steps ?? []).some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );
        const bHasParticipant = (b.steps ?? []).some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );

        if (aHasParticipant === bHasParticipant) return 0;
        return aHasParticipant ? -1 : 1;
      },
      cell: (documentVersion) => (
        <MyWorkflowStepsCell
          steps={documentVersion.steps ?? []}
          currentUserId={user.id}
        />
      ),
    },
    {
      columnKey: "commons.workflow.progress",
      header: t(($) => $.commons.workflow.progress),
      compareFn: (a, b) =>
        (a.completedSteps ?? 0) / (a.totalSteps ?? 1) -
        (b.completedSteps ?? 0) / (b.totalSteps ?? 1),
      cell: (dv) => (
        <WorkflowProgress
          completedSteps={dv.completedSteps ?? 0}
          totalSteps={dv.totalSteps ?? 0}
        />
      ),
    },
    {
      columnKey: "commons.document.reviewFrom",
      header: t(($) => $.commons.document.reviewFrom),
      compareFn: (a, b) => dateDiff(a.reviewFrom, b.reviewFrom),
      cell: (dv) => <DueDateCell dueDate={dv.reviewFrom} />,
    },
    {
      columnKey: "commons.document.reviewTo",
      header: t(($) => $.commons.document.reviewTo),
      compareFn: (a, b) => dateDiff(a.reviewTo, b.reviewTo),
      cell: (dv) => <DueDateCell dueDate={dv.reviewTo} />,
    },
    {
      columnKey: "commons.document.owner",
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) =>
        a.document.owner.firstName.localeCompare(b.document.owner.firstName),
      cell: (dv) => (
        <span>
          {dv.document.owner.firstName} {dv.document.owner.lastName}
        </span>
      ),
    },
    {
      columnKey: "commons.document.category",
      header: t(($) => $.commons.document.category),
      compareFn: (a, b) =>
        a.document.category.title.localeCompare(b.document.category.title),
      cell: (dv) => <span>{dv.document.category.title}</span>,
    },
    {
      columnKey: "commons.document.tier",
      header: t(($) => $.commons.document.tier),
      compareFn: (a, b) =>
        getTierTitle(a.document.tier?.title, t).localeCompare(
          getTierTitle(b.document.tier?.title, t),
        ),
      cell: (dv) =>
        dv.document.tier ? (
          dv.document.tier.title
        ) : (
          <span className="text-muted-foreground">{getNoTierLabel(t)}</span>
        ),
    },
  ];

  const columns = (
    activeTab === tabs.ongoing
      ? allColumns.filter((c) => c.columnKey !== "commons.document.reviewFrom")
      : allColumns.filter((c) => c.columnKey !== "commons.workflow.progress")
  ).map((c) => ({
    ...c,
    defaultSort: c.columnKey === "commons.workflow.mySteps",
  }));

  const renderData = () => {
    if (!filteredDocumentVersions?.length) {
      return <NoResult />;
    }

    return (
      <SimpleDataTable
        columns={columns}
        data={filteredDocumentVersions}
        renderExpandedRow={(dv) => <WorkflowListSteps steps={dv.steps ?? []} />}
        enablePinning={true}
        expandAll={expandAll}
        storageKey="workflows"
        wrapperClassName="border-border max-h-[calc(100vh-14rem)] border"
        headerClassName="bg-muted"
      />
    );
  };

  const setTitleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(QueryParams.Title, value);
    } else {
      params.delete(QueryParams.Title);
    }
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      <Toolbar
        left={
          <>
            <FilterPopover
              filters={filters}
              activeFiltersCount={activeFilters.length}
              onClearFilters={handleClearFilters}
              onSelectFilter={handleSelectFilter}
            />
            <SwitchWithLabel
              label={t(($) => $.commons.workflow.expandAll)}
              checked={expandAll}
              onCheckedChange={setExpandAll}
            />
            <SearchBar
              placeholder={t(($) => $.commons.workflows.search)}
              value={searchParams.get(QueryParams.Title) ?? ""}
              onDebouncedSearch={setTitleSearch}
              onSearch={setTitleSearch}
              onClearSearch={() => setTitleSearch("")}
            />
          </>
        }
      />
      {renderData()}
    </div>
  );
};
